import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Code,
  Palette,
  Zap,
  Server,
  Database,
  Atom,
  Type,
  SquareStack,
  Component,
  Wind,
  GitBranch,
  GitFork,
} from "lucide-react";
import { FaGithub } from "react-icons/fa";

function TechStackIcons() {
  const containerRef = useRef(null);
  const [containerDimensions, setContainerDimensions] = useState({
    width: 0,
    height: 0,
  });

  // Using useRef for animation values to prevent excessive re-renders
  const currentOffset = useRef({ x: 0, y: 0 });
  const currentVelocity = useRef({ x: 0, y: 0 }); // Current velocity including mouse influence
  const rawMousePos = useRef({ x: 0, y: 0 }); // Raw mouse position relative to document
  const animationFrameId = useRef(null);

  // Constants for grid and animation
  const COLS = 8;
  const ROWS = 4;
  const ICON_SIZE = 50; // Visual size of the icon (matches fontSize)
  const GRID_SPACING_X = 150; // Horizontal distance between icon centers
  const GRID_SPACING_Y = 150; // Vertical distance between icon centers

  const BASE_SPEED_X = 0.05; // Very slow base horizontal movement (pixels per frame)
  const BASE_SPEED_Y = 0.05; // Very slow base vertical movement (pixels per frame)

  // How much mouse position influences the *speed* of the grid (lower = less influence)
  // This value is CRITICAL for the "really slow" and "flow in direction of mouse" effect.
  const MOUSE_ACCELERATION_FACTOR = 0.001;

  // Smoothing factor for velocity changes to make movement fluid
  const VELOCITY_SMOOTHING = 0.1;

  // Define the list of tech icons and their corresponding Lucide components
  const techIconsMapping = useMemo(
    () => [
      { name: "HTML", icon: Code },
      { name: "CSS", icon: Palette },
      { name: "JavaScript", icon: Code },
      { name: "TypeScript", icon: Type },
      { name: "Express", icon: Zap },
      { name: "Node.js", icon: Server },
      { name: "SQL", icon: Database },
      { name: "React", icon: Atom },
      { name: "C#", icon: Code },
      { name: ".NET", icon: SquareStack },
      { name: "Bootstrap", icon: Component },
      { name: "Tailwind", icon: Wind },
      { name: "Git", icon: GitBranch },
      { name: "GitLab", icon: GitFork },
      { name: "GitHub", icon: FaGithub },
    ],
    []
  );

  // Pre-calculate the base positions for icons in a virtual infinite grid
  // We create a grid that's larger than the viewport to handle wrapping smoothly.
  // Adding +2 ensures icons are visible on both ends during wrap-around.
  const virtualGridIcons = useMemo(() => {
    const iconsArray = [];
    let iconIdx = 0;
    // Create icons for a grid slightly larger than what's strictly needed
    // to ensure seamless transitions when wrapping.
    const effectiveRows = ROWS + 2; // Extra rows for top/bottom overlap
    const effectiveCols = COLS + 2; // Extra cols for left/right overlap

    for (let r = -1; r < effectiveRows; r++) {
      // Start from -1 to ensure icons appear from "above"
      for (let c = -1; c < effectiveCols; c++) {
        // Start from -1 to ensure icons appear from "left"
        const iconData = techIconsMapping[iconIdx % techIconsMapping.length];
        iconsArray.push({
          id: `${r}-${c}-${iconData.name}`, // Unique ID
          icon: iconData.icon,
          // Base position relative to the conceptual (0,0) of the infinite grid
          baseX: c * GRID_SPACING_X,
          baseY: r * GRID_SPACING_Y,
          // Small random offset to break perfect alignment slightly
          randomOffsetX: (Math.random() - 0.5) * (GRID_SPACING_X * 0.2),
          randomOffsetY: (Math.random() - 0.5) * (GRID_SPACING_Y * 0.2),
        });
        iconIdx++;
      }
    }
    return iconsArray;
  }, [techIconsMapping]);

  // Effect to update container dimensions on mount and resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerDimensions({ width: rect.width, height: rect.height });
      }
    };

    updateDimensions(); // Set initial dimensions
    window.addEventListener("resize", updateDimensions);

    // Initialize currentOffset to a random starting position so it doesn't always start from (0,0)
    currentOffset.current.x = Math.random() * (COLS * GRID_SPACING_X);
    currentOffset.current.y = Math.random() * (ROWS * GRID_SPACING_Y);
    currentVelocity.current.x = BASE_SPEED_X;
    currentVelocity.current.y = BASE_SPEED_Y;

    return () => window.removeEventListener("resize", updateDimensions);
  }, []); // Re-run only on mount/unmount

  // Effect for animation loop (raining effect + mouse influence) and mouse tracking
  useEffect(() => {
    if (
      containerDimensions.width === 0 ||
      containerDimensions.height === 0 ||
      virtualGridIcons.length === 0
    ) {
      return; // Do not start animation if dimensions or icons are not ready
    }

    const { width: containerWidth, height: containerHeight } =
      containerDimensions;

    // Mouse movement handler (updates rawMousePos ref)
    const handleMouseMove = (e) => {
      rawMousePos.current = { x: e.clientX, y: e.clientY };
    };

    // The main animation loop
    const animate = () => {
      // Calculate center of the container in screen coordinates
      const containerRect = containerRef.current.getBoundingClientRect();
      const centerX = containerRect.left + containerWidth / 2;
      const centerY = containerRect.top + containerHeight / 2;

      // Calculate mouse position relative to container's center
      const mouseRelativeX = rawMousePos.current.x - centerX;
      const mouseRelativeY = rawMousePos.current.y - centerY;

      // Calculate target velocity based on mouse influence
      const targetVelX =
        BASE_SPEED_X + mouseRelativeX * MOUSE_ACCELERATION_FACTOR;
      const targetVelY =
        BASE_SPEED_Y + mouseRelativeY * MOUSE_ACCELERATION_FACTOR;

      // Smoothly interpolate current velocity towards target velocity
      currentVelocity.current.x +=
        (targetVelX - currentVelocity.current.x) * VELOCITY_SMOOTHING;
      currentVelocity.current.y +=
        (targetVelY - currentVelocity.current.y) * VELOCITY_SMOOTHING;

      // Update the global grid offset based on the smoothed velocity
      currentOffset.current.x += currentVelocity.current.x;
      currentOffset.current.y += currentVelocity.current.y;

      // Apply wrapping to the global offset to keep it within a manageable range
      // This creates the infinite repeating effect
      const totalVirtualGridWidth = COLS * GRID_SPACING_X;
      const totalVirtualGridHeight = ROWS * GRID_SPACING_Y;

      currentOffset.current.x =
        ((currentOffset.current.x % totalVirtualGridWidth) +
          totalVirtualGridWidth) %
        totalVirtualGridWidth;
      currentOffset.current.y =
        ((currentOffset.current.y % totalVirtualGridHeight) +
          totalVirtualGridHeight) %
        totalVirtualGridHeight;

      // Request next animation frame
      animationFrameId.current = requestAnimationFrame(animate);

      // Trigger a re-render to update icon positions
      // A dummy state update is used because currentOffset is a ref, and changing refs
      // doesn't automatically trigger re-renders.
      // This is less efficient than using state for offset, but provides more direct
      // control over animation loop without React's batching interfering.
      setContainerDimensions((prev) => ({ ...prev })); // Dummy update
    };

    document.body.addEventListener("mousemove", handleMouseMove);
    animationFrameId.current = requestAnimationFrame(animate);

    return () => {
      document.body.removeEventListener("mousemove", handleMouseMove);
      cancelAnimationFrame(animationFrameId.current);
    };
  }, [containerDimensions, virtualGridIcons]);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 w-full h-full pointer-events-none overflow-hidden"
    >
      {virtualGridIcons.map((iconData) => {
        const totalTileX = COLS * GRID_SPACING_X;
        const totalTileY = ROWS * GRID_SPACING_Y;

        // Render multiple instances of each icon to cover the entire container area and ensure no gaps
        // This is a common strategy for infinite tiling backgrounds.
        const renderInstances = [];
        for (let rowOffset = -1; rowOffset <= 1; rowOffset++) {
          // Check adjacent rows for wrapping
          for (let colOffset = -1; colOffset <= 1; colOffset++) {
            // Check adjacent cols for wrapping
            const transformedX =
              iconData.baseX +
              iconData.randomOffsetX +
              currentOffset.current.x +
              colOffset * totalTileX;
            const transformedY =
              iconData.baseY +
              iconData.randomOffsetY +
              currentOffset.current.y +
              rowOffset * totalTileY;

            // Only render if within a reasonable distance of the visible container
            if (
              transformedX > -ICON_SIZE * 2 &&
              transformedX < containerDimensions.width + ICON_SIZE * 2 &&
              transformedY > -ICON_SIZE * 2 &&
              transformedY < containerDimensions.height + ICON_SIZE * 2
            ) {
              renderInstances.push(
                <div
                  key={`${iconData.id}-${rowOffset}-${colOffset}`} // Unique key for each instance
                  className="absolute"
                  style={{
                    transform: `translate(${transformedX}px, ${transformedY}px) translate(-50%, -50%)`,
                    color: "#ffffff",
                    fontSize: `${ICON_SIZE}px`,
                    willChange: "transform",
                  }}
                >
                  {React.createElement(iconData.icon)}
                </div>
              );
            }
          }
        }
        return renderInstances;
      })}
    </div>
  );
}

export default TechStackIcons;
